import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import styles from "./styles.module.scss";
import SpacesAndGroupsList from "app/components/SpacesAndGroupsList";
import MyCoachesList from "app/components/MyCoachesList";
import RecentActivities from "app/components/RecentActivities";
import { cls } from "app/utils";
import Navbar from "app/components/Navbar";
import { useAthleteHomeModel } from "./indexModel";
import { useTranslation } from "react-i18next";
import { schedulingFetchGet } from "app/utils/request/scheduling/fetch";
import { RootState } from "typedefs";
import MyBookings from "./Components/MyBookings";
import PurchaseSuccessModal from "app/routes/CoachProfileInfo/PurchaseSuccessModal";
import AnalyzeTrialSuccessModal from "app/components/AnalyzeTrialSuccessModal";
import { PURCHASE_FAMILY_MEMBER_KEY } from "app/routes/Billing/AthleteOffering";

interface ISectionLayout {
  children: React.ReactNode;
  label: string;
  viewPressed: boolean;
  hasView?: boolean;
  viewAllHandler?: () => void;
  pending?: boolean;
}

const AthleteHome = (): React.ReactElement => {
  const history = useHistory();
  const location = useLocation();
  const self = useSelector((state: RootState) => state.user.self);

  const {
    t,
    countSpaces,
    filteredCoaches,
    onViewSpaces,
    onColapseSpaces,
    onViewNotifications,
    onViewAllCoaches,
    onColapseCoaches,
    allCoaches,
    showAllCoaches,
    loadingSpaces,
    combinedData,
    showAllSpaces,
  } = useAthleteHomeModel();

  const [coachProductStats, setCoachProductStats] = useState<{
    [coachId: number]: number;
  }>({});

  const params = new URLSearchParams(location.search);
  const [showPurchaseModal, setShowPurchaseModal] = useState(
    params.get("payment") === "success",
  );
  const [purchasedTitle] = useState(params.get("title") || "");
  const [showAnalyzeTrialModal, setShowAnalyzeTrialModal] = useState(
    params.get("trial-success") === "analyze",
  );
  const [isSubscription] = useState(params.get("offering_type") === "subscription");
  // CN-1323 (CFTB-3303): the recipient stashed before the Stripe redirect; read
  // and clear it once on the success return. Empty for a self-purchase.
  const [purchaseFamilyMember] = useState(() => {
    if (params.get("payment") !== "success") {
      return "";
    }
    const member = sessionStorage.getItem(PURCHASE_FAMILY_MEMBER_KEY);
    sessionStorage.removeItem(PURCHASE_FAMILY_MEMBER_KEY);
    return member || "";
  });

  useEffect(() => {
    if (showPurchaseModal) {
      const newParams = new URLSearchParams(location.search);
      newParams.delete("payment");
      newParams.delete("title");
      newParams.delete("offering_type");
      history.replace({ pathname: "/", search: newParams.toString() });
    }
  }, [showPurchaseModal]);

  useEffect(() => {
    if (showAnalyzeTrialModal) {
      const newParams = new URLSearchParams(location.search);
      newParams.delete("trial-success");
      history.replace({ search: newParams.toString() });
    }
  }, [showAnalyzeTrialModal]);

  useEffect(() => {
    if (self?.kind === "coach") {
      history.push("/spaces");
    }
  }, [self, history]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await schedulingFetchGet(
          "/stripe_student_products/coach_summary",
        );
        setCoachProductStats(data as { [coachId: number]: number });
      } catch {
        // Expected for athletes with no scheduling profile (e.g. self-signed,
        // not connected to any coach) — the widget just shows no stats.
      }
    };

    fetchStats();
  }, []);

  const renderData = [
    {
      label: `${t("Spaces and Groups")} (${countSpaces})`,
      hasView: combinedData?.length > 4,
      viewPressed: showAllSpaces,
      viewAllHandler: showAllSpaces ? onColapseSpaces : onViewSpaces,
      children: (
        <SpacesAndGroupsList
          spacesData={combinedData}
          pending={loadingSpaces}
          displayingAllSpaces={showAllSpaces}
        />
      ),
      pending: loadingSpaces,
    },
    {
      label: t("My Coaches") + ` (${allCoaches.length})`,
      hasView: allCoaches?.length > 4,
      viewPressed: showAllCoaches,
      viewAllHandler:
        !showAllCoaches && allCoaches?.length >= 4
          ? onViewAllCoaches
          : onColapseCoaches,
      children: (
        <MyCoachesList
          coaches={filteredCoaches}
          coachStats={coachProductStats}
        />
      ),
    },
    {
      label: t("Recent Activity"),
      hasView: true,
      viewPressed: false,
      viewAllHandler: onViewNotifications,
      children: <RecentActivities />,
    },
  ];

  return (
    <div className={cls(styles.container, "athlete-home")}>
      <Navbar label={t("Home")} />
      <MyBookings connectedCoaches={allCoaches} />
      <PurchaseSuccessModal
        isOpen={showPurchaseModal}
        subscriptionName={purchasedTitle}
        isSubscription={isSubscription}
        familyMemberName={purchaseFamilyMember}
        onClose={() => setShowPurchaseModal(false)}
        onViewPurchases={() => {
          setShowPurchaseModal(false);
          history.push("/account/athlete_subscriptions");
        }}
      />
      <AnalyzeTrialSuccessModal
        isOpen={showAnalyzeTrialModal}
        closeHandler={() => setShowAnalyzeTrialModal(false)}
      />
      {renderData.map((item, index): React.ReactElement => {
        return (
          <div
            key={item.label}
            className={index === 0 ? styles.margin_top_zero : styles.margin_top}
          >
            <SectionLayout
              label={item.label}
              viewPressed={item.viewPressed}
              hasView={item?.hasView}
              viewAllHandler={item.viewAllHandler}
              pending={item?.pending}
            >
              {item.children}
            </SectionLayout>
          </div>
        );
      })}
    </div>
  );
};

export default AthleteHome;

const SectionLayout = ({
  children,
  label,
  hasView,
  viewAllHandler,
  pending,
  viewPressed,
}: ISectionLayout) => {
  const { t } = useTranslation();

  return (
    <div>
      <div className={styles.section_title}>
        <p>{label}</p>
        {hasView && viewAllHandler && (
          <div
            onClick={!pending && viewAllHandler}
            className={cls(styles.view_all_btn, {
              [styles.disabled_view_btn]: pending,
            })}
          >
            <p>{viewPressed ? t("Collapse All") : t("View All")}</p>
          </div>
        )}
      </div>
      {children}
    </div>
  );
};
